<script lang="ts" setup>
import {computed, ref, watch} from 'vue';
import {captureException, captureMessage} from "@sentry/browser";
import QuizButton from '@/components/Quiz/QuizButton.vue'
import QuizSecureTag from '@/components/Quiz/QuizSecureTag.vue'
import {useQuizStore} from '@/stores/quizStore';
import {type IVehicle} from '@/types/vehicle'
import {appendData} from '@/services/quizService';

const {
  addresses,
  vehicles,
  selectedVehicles: storeSelectedVehicles,
  isFetching,
  getStoredData,
  setIsFetching,
  goToNextStep,
  setVehicles,
  showAlert,
  goToStepPath,
} = useQuizStore()

const selectedVehicles = ref<string[]>(storeSelectedVehicles.map(vehicle => vehicle.vin));
const anotherVehicle = ref(!!vehicles.value.length && !selectedVehicles.value.length && !!addresses.value.length);

const isValid = computed(() => !!selectedVehicles.value.length || anotherVehicle.value)

function handleSubmit() {
  if (!isValid.value) return;

  const {contact, ...payload} = getStoredData()
  payload.vehicles = payload.vehicles
    .map((vehicle: IVehicle) => ({
      ...vehicle,
      selected: selectedVehicles.value.includes(vehicle.vin)
    }))

  setIsFetching(true)
  appendData(payload)
    .then(() => {
      setVehicles(payload.vehicles)
      if (anotherVehicle.value) {
        goToStepPath(5)
      } else {
        goToNextStep()
      }
    })
    .catch(async (err) => {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on appendData: VehiclesSelection.vue");
        captureException(err);
      }
    })
    .finally(() => {
      setIsFetching(false)
    })
}

watch(() => anotherVehicle, () => {
  selectedVehicles.value = []
})

watch(() => selectedVehicles, () => {
  anotherVehicle.value = false
}, {deep: true})
</script>

<template>
  <h2 class="text-xl font-semibold mb-4"><span class="text-(--brand-primary)">Great news!</span>
    You have a potential Diesel Particulate Filter claim against Jaguar Land Rover!</h2>

  <p class="text-gray-600 mb-4 font-medium ">You could <strong>claim up to £16,000</strong> per
    vehicle listed below. Tick the box for each vehicle you’ve owned:</p>

  <form @submit.prevent="handleSubmit">
    <div class="space-y-4 mb-6">
      <label class="border border-gray-300 rounded-lg p-4 flex items-center justify-between"
             v-for="vehicle in vehicles" :key="vehicle.vin">
        <span class="text-lg grow uppercase">{{ vehicle.year }} - {{ vehicle.make }} {{
            vehicle.range
          }}</span>
        <input :value="vehicle.vin" v-model="selectedVehicles" type="checkbox"
               :disabled="anotherVehicle"
               class="min-w-5 min-h-5 h-5 w-5 text-blue-600 rounded">
      </label>

      <label class="border border-gray-300 rounded-lg p-4 flex items-center justify-between">
        <span class="text-lg grow">NONE OF THESE ARE MINE</span>
        <input v-model="anotherVehicle" type="checkbox"
               class="min-w-5 min-h-5 h-5 w-5 text-blue-600 rounded"
               :disabled="selectedVehicles.length > 0">
      </label>
    </div>

    <QuizButton label="Confirm and Continue" :disabled="!isValid || isFetching"
                :loading="isFetching"  id="vehicle-selection-submit-button"/>
  </form>

  <QuizSecureTag class="mt-6"/>
</template>
